import React, { Component } from 'react';
import { RouterProps } from 'react-router';
import { appContext } from '../../../../AppContext';
import { DateTime } from 'luxon';
import { CateringOrdersFilter, ICateringOrders, IMenuItem } from '../../../../Providers.Api/CateringOrders/CateringOrderRepository';
import { DateHelper } from '../../../../Common/DateHelper';
import { Box, Typography } from '@mui/material';
import IbssPageHeader from '../../../../Components/Forms/DateRange/IbssPageHeader';
import { toggleButtonListObject } from '../../../../Components/Inputs/ToggleButtons/IbssToggleButtons';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { GetCateringStatus } from '../../CateringOrders/List/CateringStatus';
import { ReactComponent as NoSpaceScheduleViewIcon } from '../../../OneLens/Bookings/ViewSchedule/NoSpacesScheduleView.svg';
import ScheduleCateringOrder from './ScheduleCateringOrder';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import Helper from '../../../../Common/Helper';



export default class ListPantryTimeline extends IbssPage<IProps, IState> 
{
    private get apiClient() { return appContext().apiClient; }
    private get labels() { return appContext().labels; }
    private get bookingService() { return appContext().bookingService; }
    private get appState() { return appContext().state; }
    private get local() { return appContext().localStorageProvider; }
    private helper = new Helper()


    constructor(props: IProps)
    {
        super(props);

        this.state =
        {
            buildingId: this.appState.buildingId,
            scheduleCateringOrders: [],
            scheduleOrders: [],
            spaceData: [],
            bookingDetails: [],
            singleDateFilterValue: DateHelper.now().toJSDate(),
            listViewType: "allSchedule",
            buildingStartHrs: '',
            buildingEndHrs: '',
        }
    }

    public async componentDidMount(): Promise<void>
    {
        this.appState.autoMap(this, i => ({ buildingId: i.buildingId }));
        await this.buildingChanged();
    }

    public async componentDidUpdate(prevProps: IProps, prevState: IState): Promise<void>
    {
        if (prevState.buildingId != this.state.buildingId)
        {
            this.props.history.push(`/oneLens/${this.state.buildingId}/catering/pantry-timeline`);
            await this.buildingChanged();
        }
    }

    private getBuildingHours(): void
    {
        const rootNode = this.local.getNodeData();
        const building = this.helper.getBuildingById(rootNode, this.appState.buildingId);
    
        this.setStateAsync({
            buildingStartHrs: building?.Occ_Office_hrs_stt.slice(0, -3) ?? '07:00',
            buildingEndHrs: building?.Occ_Office_hrs_stp.slice(0, -3) ?? '19:00',
        });
    }


    private async buildingChanged(): Promise<void>
    {

        this.pageTitle = this.labels.HubCateringPantryTimeline + ' ' + this.appState.buildingName;
        const payload: IFilter =
        {
            buildingId: this.state.buildingId,
            startDate: DateTime.fromJSDate(this.state.singleDateFilterValue).setZoneByNode(this.state.buildingId).startOf('day').toUTC().toISO(),
            endDate: DateTime.fromJSDate(this.state.singleDateFilterValue).setZoneByNode(this.state.buildingId).plus({ days: 1 }).startOf('day').toUTC().toISO(),
        };
        this.loadScheduleTasks(payload);
        this.getBuildingHours();
    }

    private getBookingSubjectTitle(spaceName: string, ownerEmail: string): string
    {
        return `${spaceName}, ${ownerEmail}`;
    }

    private async loadScheduleTasks(payload: IFilter): Promise<void> {
        try {
            const cateringStatus = new GetCateringStatus({});
            const tasksFilter = new CateringOrdersFilter({
                status: payload.statusValue,
                owner: payload.owner,
                taskDueFrom: DateTime.fromISO(payload.startDate),
                taskDueTo: DateTime.fromISO(payload.endDate),
            });
    
            const cateringOrders = await this.apiClient.cateringOrders.getMany(payload.buildingId, tasksFilter);
            const cateringOrdersList = cateringOrders.map(item => new CateringOrderView(item));
    
            const scheduleCateringOrders: IScheduleCateringOrders[] = cateringOrdersList.map(item => ({
                Id: item.id,
                Subject: cateringStatus.renderTaskOverAllStatusText(item.status),
                StartTime: DateHelper.fromIsoByNode(item.cateringServiceTime, this.state.buildingId).toJSDate(),
                EndTime: DateHelper.fromIsoByNode(item.cateringClearingTime, this.state.buildingId).toJSDate(),
                SpaceName: item.spaceName,
                SpaceId: item.spaceId,
                Status: item.status,
                Type: EntityType.CateringOrder,
                NodeId: item.nodeId,
            }));
    
            const bookings: IScheduleBookings[] = cateringOrdersList.map(item => ({
                Id: item.id,
                BookingId: item.bookingId,
                Subject: this.getBookingSubjectTitle(item.spaceName, item.ownerEmail),
                StartTime: DateHelper.fromIsoByNode(item.bookingStart, this.state.buildingId).toJSDate(),
                EndTime: DateHelper.fromIsoByNode(item.bookingEnd, this.state.buildingId).toJSDate(),
                SpaceName: item.spaceName,
                SpaceId: item.spaceId,
                Type: EntityType.Booking,
                NodeId: item.nodeId,
            }));
    
            const uniqueBookings = bookings.filter((bookingI, index, bookingsArray) => index === bookingsArray.findIndex(bookingJ => bookingI.BookingId === bookingJ.BookingId));
            const uniqueSpaces = cateringOrdersList.filter((cateringOrderI, index, cateringOrdersArray) => index === cateringOrdersArray.findIndex(cateringOrderJ => cateringOrderI.spaceId === cateringOrderJ.spaceId));
            const spaceData = uniqueSpaces.map(space => ({ text: space.spaceName, id: space.spaceId }));
    
            this.setState({
                scheduleCateringOrders,
                bookingDetails: uniqueBookings,
                scheduleOrders: cateringOrdersList,
                spaceData
            });
        } catch (error) 
        {
        }
    }

    private redirectPath(bookingId: string, orderId: string): void
    {
        this.props.history.push(`/operational-services-catering/${this.state.buildingId}/catering/${bookingId}/${orderId}/edit`)
    }

    private async onSingleDateFilterChanged(e: DateTime): Promise<void> 
    {
        const dateTime = e.startOf('day');
    
        await this.setStateAsync({
            singleDateFilterValue: dateTime.toJSDate(),
        });
    
        const timeZone = this.state.buildingId;

        const startDateTime = dateTime.setZoneByNode(timeZone);
        const payload: IFilter = 
        {
            buildingId: this.state.buildingId,
            startDate: startDateTime.toUTC().toISO(),
            endDate: startDateTime.plus({ days: 1 }).toUTC().toISO(),
        };
    
        this.loadScheduleTasks(payload);
    }
    

    public changeLayout(layout: string | null): void 
    {
        if(layout !== null)
        {
            this.setState({
                listViewType: layout,
            });
        }
    }


    public render(): JSX.Element 
    {

        const layoutOptions: toggleButtonListObject[] =
            [
                { value: 'allSchedule', icon: <FormatListBulletedIcon />, showFilterFields: true },
                { value: 'cateringSchedule', icon: <CalendarTodayIcon />, showFilterFields: true },
            ];

        return (
            <>
                <div className="page-height-exct-header">
                    <div className="rightPanel-main-content">
                        <IbssPageHeader
                            title={this.state.listViewType === "allSchedule" ? this.labels.funcPantryViewHeading_S : this.labels.HubMenuCateringOrders}
                            onLayoutChange={(newLayout) => this.changeLayout(newLayout)}
                            layout={this.state.listViewType}
                            toggleButtonList={layoutOptions}
                            onSingleDateChange={(e) => this.onSingleDateFilterChanged(e)}
                            showSingleDate={true}
                            singleDate={DateTime.fromJSDate(this.state.singleDateFilterValue)}
                        />
                        <Box mt={5}>
                            {this.state.spaceData.length > 0 ?
                                <ScheduleCateringOrder
                                    buildingId={this.state.buildingId}
                                    spaceData={this.state.spaceData}
                                    bookingDetails={this.state.bookingDetails}
                                    scheduleOrders={this.state.scheduleOrders}
                                    cateringOrders={this.state.scheduleCateringOrders}
                                    scheduleDate={this.state.singleDateFilterValue}
                                    loadScheduleTasks={(data: IFilter) => this.loadScheduleTasks(data)}
                                    redirectPath={(bookingId: string, orderId: string) => this.redirectPath(bookingId, orderId)}
                                    listViewType={this.state.listViewType}
                                    buildingStartHrs={this.state.buildingStartHrs}
                                    buildingEndHrs={this.state.buildingEndHrs}
                                />
                                :
                                <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <NoSpaceScheduleViewIcon />
                                </Box>
                                <Box textAlign={'center'} mt={2}>
                                    <label className="text-label">{this.labels.NoBookingswithCatering_S}</label>
                                    <Typography variant="body2" mt={2}>{this.labels.NoBookingswithCateringOnChosenDate_Message}</Typography>
                                </Box>
                            </Box>
                            }
                        </Box>
                    </div>
                </div>
            </>
        )
    }
}

interface IProps extends RouterProps { }

export interface IFilter
{
    buildingId: number;
    startDate: string;
    endDate: string;
    statusValue?: string;
    host?: string;
    owner?: string;
}

export interface IScheduleBookings 
{
    Id: number;
    BookingId: string;
    Subject: string;
    StartTime: Date;
    EndTime: Date;
    SpaceName: string;
    SpaceId: string;
    Type: EntityType;
    NodeId: number;
}

export interface IScheduleCateringOrders
{
    Id: number;
    Subject: string;
    StartTime: Date;
    EndTime: Date;
    SpaceName: string;
    SpaceId: string;
    Status: string;
    Type: EntityType;
    NodeId: number;
}

export enum EntityType
{
    Booking = 'Booking',
    CateringOrder = 'CateringOrder',
}
interface IBookingDetails
{
    nodeId: number;
    bookingId: string;
}

interface IState
{
    buildingId: number;
    scheduleOrders: CateringOrderView[];
    spaceData: {
        text: string,
        id: string,
    }[];
    scheduleCateringOrders: IScheduleCateringOrders[];
    singleDateFilterValue: Date;
    listViewType: string;
    bookingDetails: IScheduleBookings[];
    buildingStartHrs: string;
    buildingEndHrs: string;
}

export class CateringOrderView
{
    public nodeId: number;
    public id: number;
    public bookingName: string;
    public CreatedBy: string;
    public status: string;
    public spaceName: string;
    public spaceId: string;
    public cateringServiceTime: string;
    public cateringClearingTime: string;
    public bookingStart: string;
    public bookingEnd: string;
    public orderId: string;
    public menuItem: IMenuItem[];
    public bookingId: string;
    public ownerEmail: string;
    public ownerId: string;

    constructor(value: ICateringOrders)
    {
        this.nodeId = value.Node_Id;
        this.id = value.ID;
        this.bookingName = value.Booking_Name;
        this.CreatedBy = value.CreatedBy;
        this.spaceName = value.Space_Name;
        this.spaceId = value.Space_Id;
        this.cateringServiceTime = value.Catering_Service_Time.toISO();
        this.cateringClearingTime = value.Catering_Clearing_Time.toISO();
        this.bookingStart = value.Booking_Start.toISO();
        this.bookingEnd = value.Booking_End.toISO();
        this.status = value.Catering_Order_Status;
        this.orderId = value.Order_Id;
        this.menuItem = value.Menu_Items;
        this.bookingId = value.Booking_Id;
        this.ownerEmail = value.OwnedBy;
        this.ownerId = value.OwnedById;
    }
}